import { ref, readonly, computed } from 'vue'
import type { Survey } from '@/types/surveyTypes'
import * as yup from 'yup'
import { useI18n } from '@/i18n'

// Enum für Antwort-Ziele
export enum AnswerTarget {
  ANSWERS = 'answers',
  LEAD_DATA = 'leadData',
}

// Globaler State (außerhalb der Funktion) - wird von allen Instanzen geteilt
const surveyConfig = ref<Survey | null>(null)
const answers = ref<{ id: string; label: string; answer: any }[]>([])
const leadData = ref<{ id: string; answer: any }[]>([])
const currentStep = ref(0)
const stepHistory = ref<number[]>([0]) // Historie der besuchten Schritte
const stepsCount = computed(() => (surveyConfig.value ? surveyConfig.value.steps.length : 0))
const visibleQuestionIds = ref<Set<string>>(new Set())
const visibleStepIds = ref<Set<string>>(new Set())

export const useSurvey = () => {
  const { t } = useI18n()
  // Methoden die den globalen State verwalten
  const loadSurvey = (config: Survey, surveyMeta: any) => {
    console.log('Lade Umfrage-Konfiguration:', config, surveyMeta)
    surveyConfig.value = { ...config, ...surveyMeta } as Survey // Deep copy mit Type-Cast
    // Initialisiere Historie mit dem ersten Schritt
    stepHistory.value = [0]
    currentStep.value = 0
    console.log('Umfrage-Konfiguration gesetzt:', surveyConfig.value)
  }

  // Hilfsfunktion für das Setzen von Antworten in einem Ref
  const updateAnswerInRef = (
    ref: any,
    questionId: string,
    answer: any,
    includeLabel = false,
    label = '',
  ) => {
    const existingIndex = ref.value.findIndex((a: any) => a.id === questionId)
    const answerData = includeLabel ? { id: questionId, label, answer } : { id: questionId, answer }

    if (existingIndex !== -1) {
      ref.value[existingIndex] = answerData
    } else {
      ref.value.push(answerData)
    }
  }

  const setAnswer = (
    questionId: string,
    label: string = '',
    value: any,
    target: AnswerTarget = AnswerTarget.ANSWERS,
  ) => {
    console.log(`Setze Antwort für Frage '${questionId}':`, value, `Ziel: ${target}`)

    switch (target) {
      case AnswerTarget.LEAD_DATA:
        updateAnswerInRef(leadData, questionId, value)
        break
      case AnswerTarget.ANSWERS:
      default:
        updateAnswerInRef(answers, questionId, value, true, label)
        break
    }
  }

  const goToStep = (stepIndex: number) => {
    if (!surveyConfig.value) return

    // Finde den nächsten sichtbaren Step
    const visibleSteps = surveyConfig.value.steps.filter(shouldShowStep)

    if (stepIndex < 0 || stepIndex >= visibleSteps.length) return

    // Setze currentStep auf den Index im Original-Array
    const targetStep = visibleSteps[stepIndex]
    const originalIndex = surveyConfig.value.steps.findIndex((step) => step.id === targetStep.id)

    currentStep.value = originalIndex

    // Füge nur neue Schritte zur Historie hinzu (nicht bei Rückwärts-Navigation)
    const lastHistoryStep = stepHistory.value[stepHistory.value.length - 1]
    if (originalIndex !== lastHistoryStep) {
      stepHistory.value.push(originalIndex)
    }

    updateVisibility()
  }

  const goToNextVisibleStep = () => {
    if (!surveyConfig.value) return

    const currentStepObj = surveyConfig.value.steps[currentStep.value]
    const visibleSteps = surveyConfig.value.steps.filter(shouldShowStep)
    const currentVisibleIndex = visibleSteps.findIndex((step) => step.id === currentStepObj.id)

    if (currentVisibleIndex < visibleSteps.length - 1) {
      const nextStep = visibleSteps[currentVisibleIndex + 1]
      const nextIndex = surveyConfig.value.steps.findIndex((step) => step.id === nextStep.id)
      currentStep.value = nextIndex

      // Füge neuen Schritt zur Historie hinzu
      stepHistory.value.push(nextIndex)

      updateVisibility()
      return false // Nicht fertig
    } else {
      // Letzter sichtbarer Step erreicht
      return true // Survey fertig
    }
  }

  const goToPreviousStep = () => {
    if (stepHistory.value.length <= 1) return // Kein vorheriger Schritt verfügbar

    if (!surveyConfig.value) return

    // Sammle alle Fragen-IDs des aktuellen Steps
    const currentStepObj = surveyConfig.value.steps[currentStep.value] ?? { questions: [] }
    const currentStepQuestionIds = currentStepObj.questions.map((q) => q.id)

    // Entferne Antworten des aktuellen Steps
    answers.value = answers.value.filter((answer) => !currentStepQuestionIds.includes(answer.id))
    leadData.value = leadData.value.filter((answer) => !currentStepQuestionIds.includes(answer.id))

    // Entferne aktuellen Schritt aus der Historie
    stepHistory.value.pop()

    // Gehe zum vorherigen Schritt in der Historie
    const previousStep = stepHistory.value[stepHistory.value.length - 1]
    currentStep.value = previousStep

    updateVisibility()
  }

  const updateVisibility = () => {
    if (!surveyConfig.value) return

    // Update visible steps
    visibleStepIds.value.clear()
    surveyConfig.value.steps.forEach((step) => {
      if (shouldShowStep(step)) {
        visibleStepIds.value.add(step.id)
      }
    })

    // Update visible questions (existing logic)
    visibleQuestionIds.value.clear()
    surveyConfig.value.steps.forEach((step) => {
      if (shouldShowStep(step)) {
        step.questions.forEach((question) => {
          if (shouldShowQuestion(question)) {
            visibleQuestionIds.value.add(question.id)
          }
        })
      }
    })
  }

  const createStepValidationSchema = (stepIndex: number) => {
    if (!surveyConfig.value || stepIndex >= surveyConfig.value.steps.length) {
      return yup.object({})
    }

    const step = surveyConfig.value.steps[stepIndex]

    const schema = createSchema(step)

    return yup.object(schema)
  }

  const createSchema = (step: any) => {
    const schema: Record<string, any> = {}

    step?.questions.forEach((question: any) => {
      // Nur sichtbare Fragen validieren
      if (!shouldShowQuestion(question)) {
        return
      }

      let validator: any

      switch (question.type) {
        case 'text':
          validator = yup.string().nullable()
          if (question.required) {
            validator = validator.required(t('fieldRequired'))
          }
          break

        case 'number':
          validator = yup.number().nullable().typeError(t('numberInvalid'))
          if (question.min !== undefined) {
            validator = validator.min(question.min, t('numberMin', { min: question.min }))
          }
          if (question.max !== undefined) {
            validator = validator.max(question.max, t('numberMax', { max: question.max, unit: question.unit ?? '' }))
          }
          if (question.required) {
            validator = validator.required(t('fieldRequired'))
          }
          break

        case 'single_choice':
          validator = yup.string().nullable()
          if (question.required) {
            validator = validator.required(t('selectRequired'))
          }
          break

        case 'multi_choice':
          validator = yup.array()
          if (question.required) {
            validator = validator.min(1, t('multiChoiceRequired'))
          }
          if (question.min_select) {
            validator = validator.min(
              question.min_select,
              t('multiChoiceMinRequired', { min: question.min_select }),
            )
          }
          if (question.max_select) {
            validator = validator.max(
              question.max_select,
              t('multiChoiceMaxAllowed', { max: question.max_select }),
            )
          }
          break

        case 'slider':
          validator = yup.number().nullable().typeError(t('sliderRequired'))
          if (question.min !== undefined) {
            validator = validator.min(question.min, t('numberMin', { min: question.min }))
          }
          if (question.max !== undefined) {
            validator = validator.max(question.max, t('numberMax', { max: question.max, unit: '' }))
          }
          if (question.required) {
            validator = validator.required(t('fieldRequired'))
          }
          break

        default:
          validator = yup.mixed().nullable()
          if (question.required) {
            validator = validator.required(t('fieldRequired'))
          }
      }

      schema[question.id] = validator
    })

    return schema
  }

  const getCurrentStepAnswers = () => {
    if (!surveyConfig.value) return {}

    const step = surveyConfig.value.steps[currentStep.value]
    const stepAnswers: Record<string, any> = {}

    step?.questions.forEach((question) => {
      const answer = answers.value.find((a) => a.id === question.id)
      stepAnswers[question.id] = answer?.answer || null
    })

    return stepAnswers
  }

  const shouldShowStep = (step: any) => {
    // Wenn keine Bedingung definiert ist, zeige den Step immer
    if (!step.if_question || !step.if_answer) {
      return true
    }

    // Finde die Antwort auf die referenzierte Frage
    const conditionAnswer = answers.value.find((a) => a.id === step.if_question)

    if (!conditionAnswer || conditionAnswer.answer === null || conditionAnswer.answer === '') {
      return false
    }

    // Verwende die gleiche Logik wie bei Questions
    if (Array.isArray(step.if_answer)) {
      if (Array.isArray(conditionAnswer.answer)) {
        return step.if_answer.some((val: string) => conditionAnswer.answer.includes(val))
      } else {
        return step.if_answer.includes(conditionAnswer.answer)
      }
    } else {
      if (Array.isArray(conditionAnswer.answer)) {
        return conditionAnswer.answer.includes(step.if_answer)
      } else {
        return conditionAnswer.answer === step.if_answer
      }
    }
  }

  const shouldShowQuestion = (question: any) => {
    // Wenn keine Bedingung definiert ist, zeige die Frage immer
    if (!question.if_question || !question.if_answer) {
      return true
    }

    // Finde die Antwort auf die referenzierte Frage
    const conditionAnswer = answers.value.find((a) => a.id === question.if_question)

    if (!conditionAnswer || conditionAnswer.answer === null || conditionAnswer.answer === '') {
      return false
    }

    // Prüfe ob if_answer ein Array oder ein String ist
    if (Array.isArray(question.if_answer)) {
      // Array: Prüfe ob die Antwort in dem Array enthalten ist
      if (Array.isArray(conditionAnswer.answer)) {
        // Multi-Choice: Prüfe ob sich die Arrays überschneiden
        return question.if_answer.some((val: string) => conditionAnswer.answer.includes(val))
      } else {
        // Single-Choice: Prüfe ob der Wert im Array enthalten ist
        return question.if_answer.includes(conditionAnswer.answer)
      }
    } else {
      // String: Direkter Vergleich
      if (Array.isArray(conditionAnswer.answer)) {
        // Multi-Choice: Prüfe ob der String in der Antwort-Array enthalten ist
        return conditionAnswer.answer.includes(question.if_answer)
      } else {
        // Single-Choice: Direkter String-Vergleich
        return conditionAnswer.answer === question.if_answer
      }
    }
  }

  const cleanupInvisibleAnswers = () => {
    if (!surveyConfig.value) return

    // Sammle alle aktuell sichtbaren Fragen-IDs

    surveyConfig.value.steps.forEach((step) => {
      step.questions.forEach((question) => {
        if (shouldShowQuestion(question)) {
          visibleQuestionIds.value.add(question.id)
        }
      })
    })

    console.log('Sichtbare Fragen:', Array.from(visibleQuestionIds.value))
    console.log(
      'Aktuelle Antworten:',
      answers.value.map((a) => a.id),
    )

    // Entferne Antworten für nicht-sichtbare Fragen
    const oldAnswersCount = answers.value.length
    answers.value = answers.value.filter((answer) => visibleQuestionIds.value.has(answer.id))

    console.log(`Cleanup: ${oldAnswersCount} -> ${answers.value.length} Antworten`)
  }

  return {
    surveyConfig: surveyConfig, // Read-only für Komponenten
    answers: readonly(answers),
    visibleQuestionIds,
    visibleStepIds,
    currentStep,
    stepHistory: readonly(stepHistory),
    stepsCount,
    setAnswer,
    loadSurvey,
    goToStep,
    goToNextVisibleStep,
    goToPreviousStep,
    updateVisibility,
    createStepValidationSchema,
    getCurrentStepAnswers,
    shouldShowStep,
    shouldShowQuestion,
    cleanupInvisibleAnswers,
  }
}
